<template>
    <section class="px-4 py-8 mx-auto max-w-5xl lg:mt-10 lg:mb-16 xl:px-16 bg-lifeboost-blue-gray lg:py-16">
        <h3 class="mb-4 text-2xl leading-none text-center lg:mb-8 sm:text-3xl md:text-32 font-ww-bold xl:px-12" v-html="mainHeading">
        </h3>
        <div class="grid grid-cols-12 gap-y-8 mx-auto max-w-4xl sm:px-4 xl:px-6">
            <div class="col-span-12 lg:pr-6 lg:col-span-7">
                <div class="text-base !leading-5 text-left md:text-lg font-ww-regular" v-html="introText">
                </div>
                <ul class="mt-6 space-y-1 text-base md:text-lg">
                    <li v-for="ingredient in separateIngredients" :key="ingredient.id" 
                        :class="[
                            'flex justify-between w-full text-black text-balance',
                            ingredient.isMultiLine ? 'items-start' : 'items-center'
                        ]">
                        <span :class="[
                            'flex gap-x-2 text-sm whitespace-nowrap sm:text-base lg:text-17',
                            ingredient.isMultiLine ? 'items-start !leading-5' : 'items-center'
                        ]">
                            <span :class="[
                                'flex justify-center items-center text-xs',
                                ingredient.isMultiLine ? 'pt-0.5' : ''
                            ]">●</span>
                            <span class="xs-max:max-w-40 xs-max:text-wrap" v-html="ingredient.name"></span>
                        </span>
                        <div v-if="ingredient.showLine" :class="[
                            'relative flex-grow mx-2 border-b border-black',
                            ingredient.isMultiLine ? '-bottom-4' : '-bottom-2'
                        ]"></div>
                        <span v-if="ingredient.price" :class="[
                            'text-sm lg:text-17 sm:text-base',
                            ingredient.isMultiLine ? 'whitespace-nowrap !leading-5 text-right' : 'whitespace-nowrap'
                        ]" v-html="ingredient.price"></span>
                    </li>
                </ul>
                <div class="mt-4 w-full h-0.5 bg-black"></div>
                <div class="mt-2 w-full h-0.5 bg-black"></div>
                <div class="mt-2 text-2xl text-right lg:text-3xl font-ww-bold" v-html="totalPrice">
                </div>
            </div>
            <div class="col-span-12 lg:pl-6 lg:col-span-5">
                <div class="flex justify-between items-end">
                    <div class="text-2xl lg:text-3xl font-ww-bold" v-html="productName">
                    </div>
                    <div>
                        <nuxt-img 
                            :src="productImageSrc" 
                            :alt="productImageAlt" 
                            :width="productImageWidth" 
                            :height="productImageHeight"
                            loading="lazy" />
                    </div>
                </div>
                <ul class="mt-6 mb-10">
                    <li v-for="ingredient in mindflowIngredients" :key="ingredient.id" class="flex justify-between items-center w-full text-black text-balance">
                        <span class="flex gap-x-2 items-center text-sm whitespace-nowrap sm:text-base lg:text-17">
                            <span class="flex justify-center items-center text-xs">●</span>
                            <span v-html="ingredient.name"></span>
                        </span>
                        <div class="relative -bottom-2 flex-grow mx-2 border-b border-black"></div>
                        <span class="whitespace-nowrap">
                            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="7.30176" cy="7.74121" r="6.93262" fill="#F1574B" />
                                <path d="M6.29932 9.80188L4.46988 7.97243C4.39285 7.89541 4.39285 7.7606 4.46988 7.66432L4.75874 7.37546C4.83577 7.29843 4.97057 7.29843 5.06686 7.37546L6.45338 8.76198L9.53454 5.68082C9.61157 5.60379 9.74637 5.60379 9.84266 5.68082L10.1315 5.96968C10.2085 6.04671 10.2085 6.18151 10.1315 6.27779L6.60744 9.80188C6.51115 9.8789 6.37635 9.8789 6.29932 9.80188Z" fill="white" />
                            </svg>
                        </span>
                    </li>
                </ul>
                <div class="mt-8 w-full h-0.5 bg-black"></div>
                <div class="mt-2 w-full h-0.5 bg-black"></div>
                <div class="flex justify-between items-center mt-2 text-right">
                    <span class="text-base font-ww-regular" v-html="priceLabel">
                    </span>
                    <span class="text-2xl lg:text-3xl font-ww-bold" v-html="mindflowPrice">
                    </span>
                </div>
            </div>
        </div>
        <div class="mx-auto max-w-2xl text-base text-center font-ww-regular !leading-5 md:text-lg mt-10 md:px-10 lg:text-lg" v-html="bottomText">
        </div>
        <div class="mt-4 text-center md:mt-10">
                <button
                    @click="scrollTo(buttonHref,'bottom')"
                    class="inline-flex justify-center items-center px-8 py-4 text-lg font-bold text-white rounded-full transition-colors duration-200 lg:w-7/12 font-ww-medium btn-red hover:bg-red-600">
                    {{ buttonText }}
                    <svg  class="ml-2 w-5 h-5" width="22" height="15" viewBox="0 0 22 15" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M13.3007 0.50283C13.2069 0.595793 13.1325 0.706394 13.0818 0.828253C13.031 0.950112 13.0049 1.08082 13.0049 1.21283C13.0049 1.34484 13.031 1.47555 13.0818 1.59741C13.1325 1.71927 13.2069 1.82987 13.3007 1.92283L17.9007 6.52283L1.5 6.33529C1.23478 6.33529 0.98043 6.44065 0.792893 6.62819C0.605357 6.81572 0.5 7.07008 0.5 7.33529C0.5 7.60051 0.605357 7.85486 0.792893 8.0424C0.98043 8.22994 1.23478 8.33529 1.5 8.33529L17.8807 8.52283L13.3007 13.0928C13.1144 13.2802 13.0099 13.5336 13.0099 13.7978C13.0099 14.062 13.1144 14.3155 13.3007 14.5028C13.488 14.6891 13.7415 14.7936 14.0057 14.7936C14.2699 14.7936 14.5233 14.6891 14.7107 14.5028L21.0707 8.14283C21.1563 8.0607 21.2244 7.96211 21.2709 7.85299C21.3174 7.74386 21.3414 7.62646 21.3414 7.50783C21.3414 7.3892 21.3174 7.2718 21.2709 7.16267C21.2244 7.05355 21.1563 6.95496 21.0707 6.87283L14.7207 0.50283C14.6277 0.409101 14.5171 0.334707 14.3952 0.283938C14.2734 0.23317 14.1427 0.207031 14.0107 0.207031C13.8787 0.207031 13.748 0.23317 13.6261 0.283938C13.5042 0.334707 13.3936 0.409101 13.3007 0.50283Z" fill="white"/>
</svg>
                </button>
            </div>
    </section>
</template>

<script setup>
defineProps({
  mainHeading: {
    type: String,
    default: 'Ingredient Cost Breakdown: <br>What If You Bought Each Ingredient Separately?'
  },
  introText: {
    type: String,
    default: 'Let\'s do some math… If you went to Amazon today and purchased each of these ingredients individually, here\'s what you\'d pay:'
  },
  separateIngredients: {
    type: Array,
    default: () => [
      {
        id: 1,
        name: 'Instant Organic Coffee',
        price: '$20',
        showLine: true,
        isMultiLine: false
      },
      {
        id: 2,
        name: 'Grass-Fed Collagen',
        price: '$20',
        showLine: true,
        isMultiLine: false
      },
      {
        id: 3,
        name: 'Organic Lion\'s Mane Extract',
        price: '$25',
        showLine: true,
        isMultiLine: false
      },
      {
        id: 4,
        name: 'Organic Chaga Extract',
        price: '$22',
        showLine: true,
        isMultiLine: false
      },
      {
        id: 5,
        name: 'CognatiQ® Coffee Fruit',
        price: 'Approx $40',
        showLine: true,
        isMultiLine: false
      },
      {
        id: 6,
        name: 'Extract (Can\'t purchase separately)',
        price: '',
        showLine: false,
        isMultiLine: false
      },
      {
        id: 7,
        name: 'GeniusPure® Alpha-GPC (Can\'t <br> purchase separately)',
        price: 'Approx $25 for <br> non trademarked <br> version',
        showLine: true,
        isMultiLine: true
      },
      {
        id: 8,
        name: 'ElectroPrime® Electrolytes (Can\'t <br>purchase separately)',
        price: 'Approx $15 for <br> non trademarked <br> version',
        showLine: true,
        isMultiLine: true
      }
    ]
  },
  totalPrice: {
    type: String,
    default: 'Total $167+'
  },
  productName: {
    type: String,
    default: 'Mindflow'
  },
  productImageSrc: {
    type: String,
    default: '/assets/images/mindflow-table-img.png'
  },
  productImageAlt: {
    type: String,
    default: 'Mindflow'
  },
  productImageWidth: {
    type: Number,
    default: 115
  },
  productImageHeight: {
    type: Number,
    default: 155
  },
  mindflowIngredients: {
    type: Array,
    default: () => [
      {
        id: 1,
        name: 'Instant Organic Coffee'
      },
      {
        id: 2,
        name: 'Grass-Fed Collagen'
      },
      {
        id: 3,
        name: 'Organic Lion\'s Mane Extract'
      },
      {
        id: 4,
        name: 'Organic Chaga Extract'
      },
      {
        id: 5,
        name: 'CognatiQ® Coffee Fruit Extract'
      },
      {
        id: 6,
        name: 'GeniusPure® Alpha-GPC'
      },
      {
        id: 7,
        name: 'ElectroPrime® Electrolytes'
      }
    ]
  },
  priceLabel: {
    type: String,
    default: 'Yours as low as'
  },
  mindflowPrice: {
    type: String,
    default: '24.99/bag'
  },
  bottomText: {
    type: String,
    default: 'With Neuroflow, you get all of these brain-boosting ingredients in one simple, delicious cup—at a fraction of the cost. Save money, save time, and unlock your brain\'s full potential effortlessly!'
  },
  buttonText: {
    type: String,
    default: 'ORDER NOW'
  },
  buttonHref: {
    type: String,
    default: 'products'
  }
})

defineBuilderComponent({
    friendlyName: 'Price Comparison',
    canHaveChildren: false,
    inputs: [
        {
            name: 'mainHeading',
            type: 'richText',
            defaultValue: 'Ingredient Cost Breakdown: <br>What If You Bought Each Ingredient Separately?'
        },
        {
            name: 'introText',
            type: 'richText',
            defaultValue: 'Let\'s do some math… If you went to Amazon today and purchased each of these ingredients individually, here\'s what you\'d pay:'
        },
        {
            name: 'separateIngredients',
            type: 'list',
            subFields: [
                {
                    name: 'id',
                    type: 'number',
                    defaultValue: 1
                },
                {
                    name: 'name',
                    type: 'richText',
                    defaultValue: ''
                },
                {
                    name: 'price',
                    type: 'richText',
                    defaultValue: ''
                },
                {
                    name: 'showLine',
                    type: 'boolean',
                    defaultValue: true
                },
                {
                    name: 'isMultiLine',
                    type: 'boolean',
                    defaultValue: false
                }
            ],
            defaultValue: [
                {
                    id: 1,
                    name: 'Instant Organic Coffee',
                    price: '$20',
                    showLine: true,
                    isMultiLine: false
                },
                {
                    id: 2,
                    name: 'Grass-Fed Collagen',
                    price: '$20',
                    showLine: true,
                    isMultiLine: false
                },
                {
                    id: 3,
                    name: 'Organic Lion\'s Mane Extract',
                    price: '$25',
                    showLine: true,
                    isMultiLine: false
                },
                {
                    id: 4,
                    name: 'Organic Chaga Extract',
                    price: '$22',
                    showLine: true,
                    isMultiLine: false
                },
                {
                    id: 5,
                    name: 'CognatiQ® Coffee Fruit',
                    price: 'Approx $40',
                    showLine: true,
                    isMultiLine: false
                },
                {
                    id: 6,
                    name: 'Extract (Can\'t purchase separately)',
                    price: '',
                    showLine: false,
                    isMultiLine: false
                },
                {
                    id: 7,
                    name: 'GeniusPure® Alpha-GPC (Can\'t <br> purchase separately)',
                    price: 'Approx $25 for <br> non trademarked <br> version',
                    showLine: true,
                    isMultiLine: true
                },
                {
                    id: 8,
                    name: 'ElectroPrime® Electrolytes (Can\'t <br>purchase separately)',
                    price: 'Approx $15 for <br> non trademarked <br> version',
                    showLine: true,
                    isMultiLine: true
                }
            ]
        },
        {
            name: 'totalPrice',
            type: 'text',
            defaultValue: 'Total $167+'
        },
        {
            name: 'productName',
            type: 'text',
            defaultValue: 'Mindflow'
        },
        {
            name: 'productImageSrc',
            type: 'file',
            defaultValue: '/assets/images/mindflow-table-img.png'
        },
        {
            name: 'productImageAlt',
            type: 'text',
            defaultValue: 'Mindflow'
        },
        {
            name: 'productImageWidth',
            type: 'number',
            defaultValue: 115
        },
        {
            name: 'productImageHeight',
            type: 'number',
            defaultValue: 155
        },
        {
            name: 'mindflowIngredients',
            type: 'list',
            subFields: [
                {
                    name: 'id',
                    type: 'number',
                    defaultValue: 1
                },
                {
                    name: 'name',
                    type: 'richText',
                    defaultValue: ''
                }
            ],
            defaultValue: [
                {
                    id: 1,
                    name: 'Instant Organic Coffee'
                },
                {
                    id: 2,
                    name: 'Grass-Fed Collagen'
                },
                {
                    id: 3,
                    name: 'Organic Lion\'s Mane Extract'
                },
                {
                    id: 4,
                    name: 'Organic Chaga Extract'
                },
                {
                    id: 5,
                    name: 'CognatiQ® Coffee Fruit Extract'
                },
                {
                    id: 6,
                    name: 'GeniusPure® Alpha-GPC'
                },
                {
                    id: 7,
                    name: 'ElectroPrime® Electrolytes'
                }
            ]
        },
        {
            name: 'priceLabel',
            type: 'text',
            defaultValue: 'Yours as low as'
        },
        {
            name: 'mindflowPrice',
            type: 'text',
            defaultValue: '24.99/bag'
        },
        {
            name: 'bottomText',
            type: 'richText',
            defaultValue: 'With Neuroflow, you get all of these brain-boosting ingredients in one simple, delicious cup—at a fraction of the cost. Save money, save time, and unlock your brain\'s full potential effortlessly!'
        },
        {
            name: 'buttonText',
            type: 'text',
            defaultValue: 'ORDER NOW'
        },
        {
            name: 'buttonHref',
            type: 'text',
            defaultValue: 'products'
        }
    ]
})
</script>